/**
 * Read state for thread replies in Admin Coaching channels, kept per root message.
 *
 * Values are Firestore `createdAt` millis — never a device clock, so a skewed machine can neither
 * hide a reply nor announce one twice. The marks themselves live in the `chatThreadSeen` collection
 * (see `Model/ChatThreadSeen`), which is what lets one coach see the same queue on web and mobile.
 * Everything here is pure: it takes the map, it never fetches or stores it.
 */

/**
 * Millis of a reply's server timestamp, or 0 when it has none yet (a pending local write).
 * @param {Object} reply Chat message
 * @returns {Number} Timestamp in millis
 */
const replyTime = (reply) => reply.createdAt?.getTime?.() ?? 0;

/**
 * Whether this is the coach's first sight of the channel, and the watermark to write if so.
 *
 * `wasEmpty` is a conjunction on purpose. Every coach who used the inbox before `seenBefore`
 * existed has a populated map and no watermark — keying on the watermark alone would call that a
 * first sight, re-seed, and bury a live backlog. Requiring both to be absent means the rollout
 * needs no migration. Mobile carries the identical helper; keep the two in step.
 * @param {Object} seenByRoot Read marks, keyed by root message id
 * @param {Number} [seenBefore] Channel-wide read watermark in millis, undefined when unset
 * @param {Array} replies Recent thread replies in the channel
 * @returns {Object} `{ wasEmpty, watermark }`
 */
const getThreadFirstSight = (seenByRoot, seenBefore, replies) => ({
  wasEmpty: seenBefore === undefined && Object.keys(seenByRoot).length === 0,
  watermark: Math.max(0, ...replies.map(replyTime)),
});

/**
 * Thread replies worth announcing: someone else's, and newer than the last reply the coach saw on
 * that same root. Seen state is per root, so reading one thread cannot silently clear the others.
 * Whether the root is currently rendered is deliberately not considered — the loaded window grows as
 * history pages in, so "root is loaded" stopped meaning "root is on screen".
 *
 * Filters on `userId` alone. A Slack-mirrored reply is written as the Avida system user with
 * `actualSenderId` set to the resolved Slack sender, and that field is observed carrying the
 * channel's own coach id — matching on it would silence real CAM replies.
 * @param {Object} params Params
 * @param {Array} params.replies Recent thread replies in the channel
 * @param {Object} params.seenByRoot Read marks, keyed by root message id
 * @param {Number} [params.seenBefore] Channel-wide read watermark in millis, undefined when unset
 * @param {String} params.currentUserId Viewer's user id
 * @returns {Array} Unseen replies, newest first
 */
const pickUnseenThreadReplies = ({
  replies, seenByRoot, seenBefore, currentUserId,
}) => replies
  .filter((reply) => {
    const rootId = reply.parent?.id;
    if (!rootId || !reply.createdAt || reply.userId === currentUserId) {
      return false;
    }
    // The floor is the coarse channel watermark, or this root's own mark when the coach has read
    // further on that thread. An absent watermark reads as 0, never as "now".
    return replyTime(reply) > Math.max(seenBefore ?? 0, seenByRoot[rootId] ?? 0);
  })
  .sort((a, b) => replyTime(b) - replyTime(a));

/**
 * One row per thread, built entirely from the replies already in hand.
 *
 * Every reply denormalizes its root as `parent`, so the root's preview text comes free with the
 * reply — no second read per thread, and no N+1 when the inbox lists twenty of them. The unread
 * count is the reply count after the same filter the notice uses, so a row showing "3 new" and the
 * pill counting that thread can never disagree.
 *
 * Rows carry `unreadCount: 0` rather than being dropped: the Recent tab lists threads regardless of
 * read state, and both tabs read from this one pass.
 *
 * `rootText` is whichever reply the iteration reached first and is never refreshed, so an edited
 * root keeps its old preview until a new reply lands. Deliberate: reading the root would cost one
 * document per row, and the row is a pointer into the thread rather than a copy of it.
 * @param {Object} params Params
 * @param {Array} params.replies Recent thread replies in the channel
 * @param {Object} params.seenByRoot Read marks, keyed by root message id
 * @param {Number} [params.seenBefore] Channel-wide read watermark in millis, undefined when unset
 * @param {String} params.currentUserId Viewer's user id
 * @returns {Array} Thread rows, newest activity first
 */
const groupThreadRows = ({
  replies, seenByRoot, seenBefore, currentUserId,
}) => {
  const unseenIds = new Set(
    pickUnseenThreadReplies({
      replies, seenByRoot, seenBefore, currentUserId,
    }).map(({ id }) => id),
  );

  const rowsByRoot = new Map();
  replies.forEach((reply) => {
    const rootId = reply.parent?.id;
    if (!rootId || !reply.createdAt) {
      return;
    }
    const at = replyTime(reply);
    const row = rowsByRoot.get(rootId) ?? {
      rootId,
      rootText: reply.parent.text || '',
      rootUserId: reply.parent.userId,
      latestReply: reply,
      latestReplyAt: at,
      unreadCount: 0,
      newestUnseenAt: 0,
    };
    if (at >= row.latestReplyAt) {
      row.latestReply = reply;
      row.latestReplyAt = at;
    }
    if (unseenIds.has(reply.id)) {
      row.unreadCount += 1;
      row.newestUnseenAt = Math.max(row.newestUnseenAt, at);
    }
    rowsByRoot.set(rootId, row);
  });

  return [...rowsByRoot.values()].sort((a, b) => b.latestReplyAt - a.latestReplyAt);
};

export {
  getThreadFirstSight,
  groupThreadRows,
  pickUnseenThreadReplies,
  replyTime,
};
